// import * as d3 from 'd3'
import { SVGPathData } from 'svg-pathdata'

const ORIGIN_VIEWBOX_XY = [669.83, 831.77]
const ORIGIN_BOARD_PATH =
  'M334.91,.51L.5,68.73c-.04,402.78-.04,538.78,334.42,762.44,334.41-223.66,334.45-359.66,334.41-762.44L334.91,.51Z'
const ORIGIN_BOARD_CMD = new SVGPathData(ORIGIN_BOARD_PATH).toAbs()

interface Vector2 {
  x: number
  y: number
}

interface CursorCalculateAttributes {
  center: Vector2
  containerWidth: number
  containerHeight: number
  svgPathData: SVGPathData
  svgPathDataString: string
  svgPathRect: {
    x: number
    y: number
    width: number
    height: number
    r: number
  }
  svgClipPathData: SVGPathData
  svgClipPathDataString: string
  mousePositionPercent: {
    x: number
    y: number
    direction: Vector2
    strength: number
  }
}

const defaultCursorCalculateAttributes: CursorCalculateAttributes = {
  center: { x: 0, y: 0 },
  containerHeight: 0,
  containerWidth: 0,
  svgPathData: new SVGPathData(ORIGIN_BOARD_CMD.commands),
  svgPathDataString: ORIGIN_BOARD_CMD.encode(),
  svgPathRect: {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    r: 0,
  },
  svgClipPathData: new SVGPathData(ORIGIN_BOARD_CMD.commands),
  svgClipPathDataString: ORIGIN_BOARD_CMD.encode(),
  mousePositionPercent: {
    x: 0,
    y: 0,
    direction: { x: 0, y: 0 },
    strength: 0,
  },
}

interface CursorOptions {
  speed: number
  scale: number
  clipScale: number
  offset: [number, number]
  colors: [string, string]
  clipOffset: [number, number]
  clipColor: string
  initialPosition: Vector2
  container: Element
  isTouchDevices: boolean
  backgroundColor: string
}

const defaultCursorOptions: CursorOptions = {
  speed: 0.1,
  scale: 1.3,
  offset: [200, 150],
  colors: ['#8c0fee', '#3effe8'],
  clipScale: 0.85,
  clipOffset: [130, 80],
  clipColor: '#181825',
  initialPosition: { x: 0, y: 0 },
  container: document.body,
  isTouchDevices: false,
  backgroundColor: 'none',
}

interface BorderMaskDefs {
  linearGradient: SVGLinearGradientElement | null
  stop1: SVGStopElement | null
  stop2: SVGStopElement | null
  maskRect: SVGRectElement | null
}

type MousePositionEvent = MouseEvent | TouchEvent

export default class Cursor {
  protected readonly options: CursorOptions
  protected readonly container: Element
  // svg origin attr
  protected readonly svgAttributes = defaultCursorCalculateAttributes
  // svg elements
  protected svg: SVGSVGElement | null = null
  protected boardPath: SVGPathElement | null = null
  protected boardMaskPath: SVGPathElement | null = null
  protected boardClipPath: SVGPathElement | null = null
  protected readonly borderMaskDefs: BorderMaskDefs = {
    linearGradient: null,
    stop1: null,
    stop2: null,
    maskRect: null,
  }

  // v2
  protected readonly mouse: Vector2
  // protected readonly pos: Vector2
  // protected readonly diff: Vector2 = { x: 0, y: 0 }

  constructor(options: Partial<CursorOptions>) {
    this.options = {
      ...defaultCursorOptions,
      ...options,
    }
    // this.pos = this.options.initialPosition
    this.mouse = this.options.initialPosition
    this.container = this.options.container

    this.handleMousePosition = this.handleMousePosition.bind(this)
    this.mousemoveCursor()
    window.addEventListener('resize', (e) => this.init())

    this.init()
    this.loop()
  }

  handleMousePosition(e: MousePositionEvent): void {
    if (this.options.isTouchDevices) return
    const ee = e as any
    let x: number
    let y: number
    if (ee.touches) {
      x = ee.touches[0].clientX
      y = ee.touches[0].clientY
    } else {
      x = ee.clientX
      y = ee.clientY
    }
    this.mouse.x = x
    this.mouse.y = y
    this.calculateMouse()
  }

  mousemoveCursor(): void {
    window.addEventListener('touchmove', this.handleMousePosition, {
      passive: true,
    })
    window.addEventListener('mousemove', this.handleMousePosition, {
      passive: true,
    })
  }

  init(): void {
    const svg = this.getSVG()
    const boardPath = this.getBoardPath()
    const boardMaskPath = this.getBoardMaskPath()
    const boardClipPath = this.getBoardClipPath()
    this.calculateSvgAttributes()
    // some dom attrs
    const { svgAttributes } = this
    svg.setAttributeNS(
      null,
      'viewBox',
      `0 0 ${svgAttributes.containerWidth} ${svgAttributes.containerHeight}`
    )
    boardPath.setAttributeNS(null, 'fill', 'url(#colorGradient)')
    boardPath.setAttributeNS(null, 'fill-opacity', '0.2')
    boardMaskPath.setAttributeNS(null, 'fill', 'url(#colorGradient)')
    boardMaskPath.setAttributeNS(null, 'mask', 'url(#opacityMask)')
    boardClipPath.setAttributeNS(null, 'fill', this.options.clipColor)
  }

  loop(): void {
    // this.setParamsDiffs()
    this.drawBoardPath()
    requestAnimationFrame(() => this.loop())
  }

  destory(): void {
    window.removeEventListener('touchmove', this.handleMousePosition)
    window.removeEventListener('mousemove', this.handleMousePosition)
  }

  getSVG(): SVGSVGElement {
    if (this.svg) return this.svg
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    this.container.appendChild(svg)
    this.svg = svg
    // ge
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
    defs.innerHTML = `
      <linearGradient id="colorGradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stop-color="${this.options.colors[0]}"></stop>
        <stop offset="100%" stop-color="${this.options.colors[1]}"></stop>
      </linearGradient>
      <linearGradient id="opacityGradient" x1="1" y1="1" x2="0" y2="1">
        <stop class="s1" offset="0.5" stop-color="#fff" stop-opacity="0" />
        <stop class="s2" offset="1" stop-color="#fff" stop-opacity="1" />
      </linearGradient>
      <mask id="opacityMask">
        <rect x="0" y="0" width="0" height="0" fill="url(#opacityGradient)" />
      </mask>
    `
    svg.appendChild(defs)
    this.borderMaskDefs.linearGradient = defs.querySelector('#opacityGradient')
    this.borderMaskDefs.stop1 = defs.querySelector('#opacityGradient .s1')
    this.borderMaskDefs.stop2 = defs.querySelector('#opacityGradient .s2')
    this.borderMaskDefs.maskRect = defs.querySelector('#opacityMask rect')
    return this.svg
  }

  getBoardPath(): SVGPathElement {
    if (this.boardPath) return this.boardPath
    const svg = this.getSVG()
    const path = document.createElementNS(
      svg.namespaceURI,
      'path'
    ) as SVGPathElement
    path.classList.add('board')
    svg.appendChild(path)
    this.boardPath = path
    return path
  }

  getBoardMaskPath(): SVGPathElement {
    if (this.boardMaskPath) return this.boardMaskPath
    const svg = this.getSVG()
    const path = document.createElementNS(
      svg.namespaceURI,
      'path'
    ) as SVGPathElement
    path.classList.add('board-mask')
    svg.appendChild(path)
    this.boardMaskPath = path
    return path
  }

  getBoardClipPath(): SVGPathElement {
    if (this.boardClipPath) return this.boardClipPath
    const svg = this.getSVG()
    const path = document.createElementNS(
      svg.namespaceURI,
      'path'
    ) as SVGPathElement
    path.classList.add('board-clip')
    svg.appendChild(path)
    this.boardClipPath = path
    return path
  }

  calculateSvgAttributes(): void {
    const { svgAttributes, container, options } = this
    const rect = container.getBoundingClientRect()
    svgAttributes.containerWidth = rect.width
    svgAttributes.containerHeight = rect.height
    const svgPathData = new SVGPathData(ORIGIN_BOARD_CMD.encode())
    svgAttributes.svgPathData = svgPathData
    svgAttributes.center = {
      x: svgAttributes.containerWidth / 2,
      y: svgAttributes.containerHeight / 2,
    }
    // 应用scale
    const originAspectRatio = ORIGIN_VIEWBOX_XY[0] / ORIGIN_VIEWBOX_XY[1]
    const containerAspectRatio =
      svgAttributes.containerWidth / svgAttributes.containerHeight
    let origin: number
    let target: number
    if (originAspectRatio >= containerAspectRatio) {
      // 竖屏，取横坐标
      origin = ORIGIN_VIEWBOX_XY[0]
      target = svgAttributes.containerWidth
    } else {
      // 横屏，取纵坐标
      origin = ORIGIN_VIEWBOX_XY[1]
      target = svgAttributes.containerHeight
    }
    const scale = (target / origin) * options.scale
    svgPathData.scale(scale, scale)
    // 生成clip
    const svgClipPathData = new SVGPathData(svgPathData.encode())
    svgAttributes.svgClipPathData = svgClipPathData
    svgClipPathData.scale(options.clipScale, options.clipScale)
    // 应用transfer
    let currentWidth = ORIGIN_VIEWBOX_XY[0] * scale
    let currentHeight = ORIGIN_VIEWBOX_XY[1] * scale
    let offsetX = svgAttributes.center.x - currentWidth / 2
    let offsetY = svgAttributes.center.y - currentHeight / 2
    svgPathData.translate(offsetX, offsetY)
    currentWidth = ORIGIN_VIEWBOX_XY[0] * scale * options.clipScale
    currentHeight = ORIGIN_VIEWBOX_XY[1] * scale * options.clipScale
    offsetX = svgAttributes.center.x - currentWidth / 2
    offsetY = svgAttributes.center.y - currentHeight / 2
    svgClipPathData.translate(offsetX, offsetY)
    // 生成d
    const bound = svgPathData.getBounds()
    svgAttributes.svgPathRect = {
      x: bound.minX,
      y: bound.minY,
      width: bound.maxX - bound.minX,
      height: bound.maxY - bound.minY,
      r: 0,
    }
    svgAttributes.svgPathRect.r =
      Math.sqrt(
        svgAttributes.svgPathRect.width ** 2 +
          svgAttributes.svgPathRect.height ** 2
      ) / 2
    svgAttributes.svgPathDataString = svgPathData.encode()
    svgAttributes.svgClipPathDataString = svgClipPathData.encode()
    // mask信息
    // this.borderMaskDefs.linearGradient
  }

  drawBoardPath(): void {
    const { svgAttributes, options, borderMaskDefs } = this
    const { offset, clipOffset } = options
    const { mousePositionPercent, svgPathRect, center } = svgAttributes
    // board
    const boardPath = this.getBoardPath()
    const svgPathData = new SVGPathData(svgAttributes.svgPathDataString)
    svgPathData.translate(
      offset[0] * mousePositionPercent.x,
      offset[1] * mousePositionPercent.y
    )
    boardPath.setAttributeNS(null, 'd', svgPathData.encode())
    // board mask
    const boardMaskPath = this.getBoardMaskPath()
    boardMaskPath.setAttributeNS(null, 'd', svgPathData.encode())
    // const { linearGradient, stop1, stop2, maskRect } = borderMaskDefs
    const { linearGradient, maskRect, stop2 } = borderMaskDefs
    maskRect?.setAttributeNS(null, 'x', (center.x - svgPathRect.r).toString())
    maskRect?.setAttributeNS(null, 'y', (center.y - svgPathRect.r).toString())
    maskRect?.setAttributeNS(null, 'width', (svgPathRect.r * 2).toString())
    maskRect?.setAttributeNS(null, 'height', (svgPathRect.r * 2).toString())
    linearGradient?.setAttributeNS(
      null,
      'x1',
      (mousePositionPercent.direction.x + 0.5).toString()
    )
    linearGradient?.setAttributeNS(
      null,
      'y1',
      (mousePositionPercent.direction.y + 0.5).toString()
    )
    linearGradient?.setAttributeNS(
      null,
      'x2',
      (-mousePositionPercent.direction.x + 0.5).toString()
    )
    linearGradient?.setAttributeNS(
      null,
      'y2',
      (-mousePositionPercent.direction.y + 0.5).toString()
    )
    stop2?.setAttributeNS(
      null,
      'stop-opacity',
      `${mousePositionPercent.strength}`
    )
    // board clip
    const boardClipPath = this.getBoardClipPath()
    const svgClipPathData = new SVGPathData(svgAttributes.svgClipPathDataString)
    svgClipPathData.translate(
      clipOffset[0] * mousePositionPercent.x,
      clipOffset[1] * mousePositionPercent.y
    )
    boardClipPath.setAttributeNS(null, 'd', svgClipPathData.encode())
  }

  calculateMouse(): void {
    const { svgAttributes } = this
    const { containerWidth, containerHeight } = svgAttributes
    const v = Math.max(containerWidth, containerHeight)
    let x: number
    let y: number
    if (containerWidth >= containerHeight) {
      x = this.mouse.x / v - 0.5
      y = (this.mouse.y + (containerWidth - containerHeight) / 2) / v - 0.5
    } else {
      x = (this.mouse.x + (containerWidth - containerHeight) / 2) / v - 0.5
      y = this.mouse.y / v - 0.5
    }
    svgAttributes.mousePositionPercent.x = x
    svgAttributes.mousePositionPercent.y = y
    const mx = -x
    const my = -y
    const rad = Math.atan2(mx, my)
    // const deg = -rad * (180 / Math.PI)
    // console.log(Math.sin(-rad) / 2, Math.cos(-rad) / 2)
    const md = Math.sqrt(Math.pow(Math.abs(mx), 2) + Math.pow(Math.abs(my), 2))
    const strength = Math.min(md / 0.5, 1)
    svgAttributes.mousePositionPercent.strength = strength
    svgAttributes.mousePositionPercent.direction.x = Math.sin(-rad) / 2
    svgAttributes.mousePositionPercent.direction.y = -Math.cos(-rad) / 2
  }

  // setParamsDiffs(): void {
  //   this.diff.x = this.mouse.x - this.pos.x
  //   this.diff.y = this.mouse.y - this.pos.y
  //   this.pos.x += this.diff.x * this.options.speed
  //   this.pos.y += this.diff.y * this.options.speed
  // }
}
